import { useState, useRef, useCallback, useEffect } from "react";
import { ChevronLeft, ChevronRight, ZoomIn, X, Maximize2 } from "lucide-react";

export default function ProductGallery({ images = [], productName = "" }) {
  const [active, setActive] = useState(0);
  const [fade, setFade] = useState(false);
  const [zoomOpen, setZoomOpen] = useState(false);
  const [zoomScale, setZoomScale] = useState(1);
  const [zoomPos, setZoomPos] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0, posX: 0, posY: 0 });
  const touchStart = useRef({ x: 0, y: 0, time: 0 });
  const mainRef = useRef(null);

  const count = images.length;
  const safeActive = Math.min(active, count - 1);
  const currentImg = images[safeActive] || images[0];

  // Smooth transition between images
  const goTo = useCallback((idx) => {
    if (idx === safeActive || count <= 1) return;
    setFade(true);
    setTimeout(() => {
      setActive(idx);
      setFade(false);
    }, 180);
  }, [safeActive, count]);

  const prev = useCallback(() => goTo(safeActive <= 0 ? count - 1 : safeActive - 1), [goTo, safeActive, count]);
  const next = useCallback(() => goTo(safeActive >= count - 1 ? 0 : safeActive + 1), [goTo, safeActive, count]);

  // Touch swipe on main image
  const onTouchStart = (e) => {
    const t = e.touches[0];
    touchStart.current = { x: t.clientX, y: t.clientY, time: Date.now() };
  };
  const onTouchEnd = (e) => {
    if (count <= 1) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - touchStart.current.x;
    const dt = Date.now() - touchStart.current.time;
    if (dt < 400 && Math.abs(dx) > 40) {
      dx < 0 ? next() : prev();
    }
  };

  // Keyboard navigation in zoom modal
  useEffect(() => {
    if (!zoomOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") { setZoomOpen(false); resetZoom(); }
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [zoomOpen, prev, next]);

  // Zoom helpers
  const resetZoom = () => { setZoomScale(1); setZoomPos({ x: 0, y: 0 }); };
  const toggleZoom = (e) => {
    e.stopPropagation();
    if (zoomScale > 1) {
      resetZoom();
    } else {
      setZoomScale(2.5);
      setZoomPos({ x: 0, y: 0 });
    }
  };

  // Pan in zoom modal
  const onMouseDownZoom = (e) => {
    if (zoomScale <= 1) return;
    e.preventDefault();
    setDragging(true);
    dragStart.current = { x: e.clientX, y: e.clientY, posX: zoomPos.x, posY: zoomPos.y };
  };
  const onMouseMoveZoom = (e) => {
    if (!dragging) return;
    const dx = e.clientX - dragStart.current.x;
    const dy = e.clientY - dragStart.current.y;
    setZoomPos({ x: dragStart.current.posX + dx, y: dragStart.current.posY + dy });
  };
  const onMouseUpZoom = () => setDragging(false);

  const openZoom = () => {
    resetZoom();
    setZoomOpen(true);
  };

  if (!count) return null;

  return (
    <div data-testid="product-gallery">
      {/* Main image container */}
      <div
        ref={mainRef}
        className="aspect-[4/5] rounded-3xl overflow-hidden bg-[#F7F5F0] relative group"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        data-testid="product-main-image"
      >
        {/* Image with fade transition */}
        <img
          src={currentImg}
          alt={productName}
          className="w-full h-full object-cover transition-all duration-300 ease-in-out cursor-zoom-in"
          style={{ opacity: fade ? 0 : 1, transform: fade ? "scale(1.02)" : "scale(1)" }}
          onClick={openZoom}
          draggable={false}
          data-testid="gallery-main-img"
        />

        {/* Arrows — visible on hover (desktop) or always on touch */}
        {count > 1 && (
          <>
            <button
              onClick={(e) => { e.stopPropagation(); prev(); }}
              className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/80 backdrop-blur-sm hover:bg-white p-2.5 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 active:scale-95"
              aria-label="Image précédente"
              data-testid="gallery-prev"
            >
              <ChevronLeft size={20} className="text-[#1F1C18]" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); next(); }}
              className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/80 backdrop-blur-sm hover:bg-white p-2.5 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 active:scale-95"
              aria-label="Image suivante"
              data-testid="gallery-next"
            >
              <ChevronRight size={20} className="text-[#1F1C18]" />
            </button>
          </>
        )}

        {/* Zoom button */}
        <button
          onClick={(e) => { e.stopPropagation(); openZoom(); }}
          className="absolute top-4 right-4 bg-white/80 backdrop-blur-sm p-2.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 shadow-lg hover:bg-white active:scale-95"
          aria-label="Agrandir"
          data-testid="gallery-zoom-btn"
        >
          <ZoomIn size={18} className="text-terracotta" />
        </button>

        {/* Counter */}
        {count > 1 && (
          <div
            className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 backdrop-blur-sm text-white text-xs px-3 py-1.5 rounded-full tracking-wider"
            data-testid="gallery-counter"
          >
            {safeActive + 1} / {count}
          </div>
        )}
      </div>

      {/* Thumbnails */}
      {count > 1 && (
        <div className="flex gap-3 mt-4 overflow-x-auto pb-1" data-testid="product-thumbnails">
          {images.map((img, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              className={`flex-shrink-0 w-20 h-20 sm:w-24 sm:h-24 rounded-2xl overflow-hidden border-2 transition-all duration-300 ${
                safeActive === i
                  ? "border-terracotta shadow-md scale-[1.03]"
                  : "border-transparent opacity-60 hover:opacity-100 hover:border-[#E5E1D8]"
              }`}
              data-testid={`thumbnail-${i}`}
            >
              <img src={img} alt={`${productName} ${i + 1}`} className="w-full h-full object-cover" draggable={false} />
            </button>
          ))}
        </div>
      )}

      {/* Zoom modal */}
      {zoomOpen && (
        <div
          className="fixed inset-0 z-[60] bg-black/95 flex flex-col"
          onClick={() => { setZoomOpen(false); resetZoom(); }}
          onMouseMove={onMouseMoveZoom}
          onMouseUp={onMouseUpZoom}
          onMouseLeave={onMouseUpZoom}
          data-testid="zoom-modal"
        >
          {/* Top bar */}
          <div className="flex items-center justify-between px-4 sm:px-6 py-4 text-white/80 z-10">
            {count > 1 && (
              <span className="text-sm tracking-wider">{safeActive + 1} / {count}</span>
            )}
            <div className="flex items-center gap-3 ml-auto">
              <button
                onClick={toggleZoom}
                className="p-2 hover:text-white transition-colors"
                aria-label={zoomScale > 1 ? "Réinitialiser le zoom" : "Zoomer"}
                data-testid="zoom-toggle"
              >
                <Maximize2 size={20} />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); setZoomOpen(false); resetZoom(); }}
                className="p-2 hover:text-white transition-colors"
                aria-label="Fermer"
                data-testid="zoom-close"
              >
                <X size={24} />
              </button>
            </div>
          </div>

          {/* Image area */}
          <div className="flex-1 flex items-center justify-center relative overflow-hidden">
            {/* Navigation arrows in modal */}
            {count > 1 && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); resetZoom(); prev(); }}
                  className="absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 z-10 bg-white/10 hover:bg-white/25 backdrop-blur p-3 rounded-full transition-all duration-200 active:scale-90"
                  aria-label="Image précédente"
                  data-testid="zoom-prev"
                >
                  <ChevronLeft size={24} className="text-white" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); resetZoom(); next(); }}
                  className="absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 z-10 bg-white/10 hover:bg-white/25 backdrop-blur p-3 rounded-full transition-all duration-200 active:scale-90"
                  aria-label="Image suivante"
                  data-testid="zoom-next"
                >
                  <ChevronRight size={24} className="text-white" />
                </button>
              </>
            )}

            {/* Zoomable image */}
            <img
              src={currentImg}
              alt={productName}
              className="max-h-[80vh] max-w-[90vw] object-contain rounded-2xl select-none transition-opacity duration-200"
              style={{
                opacity: fade ? 0 : 1,
                transform: `scale(${zoomScale}) translate(${zoomPos.x / zoomScale}px, ${zoomPos.y / zoomScale}px)`,
                cursor: zoomScale > 1 ? (dragging ? "grabbing" : "grab") : "zoom-in",
                transition: dragging ? "none" : "transform 0.3s ease-out, opacity 0.2s",
              }}
              onClick={toggleZoom}
              onMouseDown={onMouseDownZoom}
              draggable={false}
              data-testid="zoom-image"
            />
          </div>

          {/* Thumbnail strip in modal */}
          {count > 1 && (
            <div className="flex justify-center gap-2 px-4 py-4 overflow-x-auto">
              {images.map((img, i) => (
                <button
                  key={i}
                  onClick={(e) => { e.stopPropagation(); resetZoom(); goTo(i); }}
                  className={`flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-xl overflow-hidden border-2 transition-all duration-200 ${
                    safeActive === i
                      ? "border-terracotta opacity-100"
                      : "border-white/20 opacity-40 hover:opacity-80"
                  }`}
                  data-testid={`zoom-thumbnail-${i}`}
                >
                  <img src={img} alt={`${productName} ${i + 1}`} className="w-full h-full object-cover" draggable={false} />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
